import React, { useEffect, useMemo, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import RequestStatusChip from 'components/chip/RequestStatusChip';
import RequestTypeChip from 'components/chip/RequestTypeChip';
import AmountField from 'components/form/AmountField';
import CustomSelect, { SelectOption } from 'components/form/CustomSelect';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import HourField from 'components/form/HourField';
import PercentField from 'components/form/PercentField';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import ContractConfirmDialog, {
  ConfirmDialogResult,
} from 'pages/contract/components/ContractConfirmDialog';
import ContractPasteButton from 'pages/contract/components/ContractPasteButton';
import ContractRequestDuplicateDialog from 'pages/contract/components/ContractRequestDuplicateDialog';
import TemplateGroupView, { TemplateGroup } from 'pages/contract/components/TemplateGroupView';
import { UserSession } from 'stores/AuthenticationStoreAdapter';
import {
  ContractRequestDetailResult,
  RequestType,
  TemplateItemInput,
  UpdateContractRequestParams,
  approveContractRequest,
  updateContractRequest,
  updateContractRequestStatus,
} from 'stores/ContractRequestStoreAdapter';
import {
  GetContractTemplateResponse,
  getContractTemplate,
} from 'stores/ContractTemplateStoreAdapter';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import { MemberListResult } from 'stores/MemberStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { formatDate, formatDateTime } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

type ContractRequestDetailProps = {
  detail: ContractRequestDetailResult;
  userSession: UserSession;
  users: UserResult[];
  members: MemberListResult[];
  isBackOffice: boolean;
  /**
   * 更新後コールバック。
   * kind='status': ステータス更新（対応開始/承認/否決/取消）。呼び出し側はドロワーを閉じない。
   * kind='content': 内容更新（保存/申請）。呼び出し側はドロワーを閉じて再検索する。
   */
  onUpdated: (kind: 'status' | 'content') => void;
  onEditModeChange?: (isEditing: boolean) => void;
};

const ContractRequestDetail: React.FC<ContractRequestDetailProps> = ({
  detail,
  userSession,
  users,
  members,
  isBackOffice,
  onUpdated,
  onEditModeChange,
}) => {
  const [confirmDialog, setConfirmDialog] = useState<'approve' | 'reject' | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);

  // マスタデータ（編集モード用）
  const [prefectures, setPrefectures] = useState<MasterTypeResult[]>([]);
  const [contractTypes, setContractTypes] = useState<MasterTypeResult[]>([]);
  const [contractUnits, setContractUnits] = useState<MasterTypeResult[]>([]);
  const [templateData, setTemplateData] = useState<GetContractTemplateResponse | null>(null);
  const [masterLoaded, setMasterLoaded] = useState(false);

  // 編集フォーム値
  const [editPersonInChargeId, setEditPersonInChargeId] = useState<string | null>(null);
  const [editPrefectureId, setEditPrefectureId] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editContractTypeId, setEditContractTypeId] = useState('');
  const [editContractStartDate, setEditContractStartDate] = useState('');
  const [editContractEndDate, setEditContractEndDate] = useState('');
  const [editCostAmount, setEditCostAmount] = useState('');
  const [editSalesAmount, setEditSalesAmount] = useState('');
  const [editOperationRate, setEditOperationRate] = useState('');
  const [editContractUnitId, setEditContractUnitId] = useState('');
  const [editAdjustmentUpperLimit, setEditAdjustmentUpperLimit] = useState('');
  const [editAdjustmentLowerLimit, setEditAdjustmentLowerLimit] = useState('');
  const [editNeedsOriginSheet, setEditNeedsOriginSheet] = useState(false);
  const [editOriginSheetDestination, setEditOriginSheetDestination] = useState('');
  const [editRemarks, setEditRemarks] = useState('');
  const [editTemplateValues, setEditTemplateValues] = useState<Record<string, string>>({});
  const [editTechStackIds, setEditTechStackIds] = useState<string[]>([]);

  // テンプレートデータと都道府県マスタを初回マウント時に取得（閲覧モードでも表示するため）
  useEffect(() => {
    if (!templateData) {
      getContractTemplate({ visibleOnly: true })
        .then((tmpl) => setTemplateData(tmpl))
        .catch(() => {});
    }
    if (prefectures.length === 0) {
      getMasterType('prefecture')
        .then((pref) => setPrefectures(pref))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isApplicant = detail.applicants.some((a) => a.userId === userSession.userId);

  // ボタン表示制御
  const showEdit = (() => {
    if (detail.requestStatus === 'ADJUSTING' && isApplicant) return true;
    if (detail.requestStatus === 'IN_PROGRESS' && isBackOffice) return true;
    if (detail.requestStatus === 'REJECTED' && isApplicant) return true;
    return false;
  })();

  // 複製ボタン表示: 新規申請 + 調整中 + 申請者
  const showDuplicate =
    detail.requestType === 'CREATE' && detail.requestStatus === 'ADJUSTING' && isApplicant;

  // 再申請可能なステータス
  const canResubmit =
    (detail.requestStatus === 'ADJUSTING' || detail.requestStatus === 'REJECTED') && isApplicant;

  const showCancel = (() => {
    if (detail.requestStatus === 'ADJUSTING' && isApplicant) return true;
    if (detail.requestStatus === 'REJECTED' && isApplicant) return true;
    return false;
  })();

  const showStart = detail.requestStatus === 'SUBMITTED' && isBackOffice;
  const showReject = detail.requestStatus === 'IN_PROGRESS' && isBackOffice;
  const showApprove = detail.requestStatus === 'IN_PROGRESS' && isBackOffice;
  const approveEnabled = detail.checklists.every((c) => c.isChecked);

  const handleStatusChange = (targetStatus: 'IN_PROGRESS' | 'REJECTED' | 'CANCELLED') => {
    updateContractRequestStatus({
      contractRequestId: detail.contractRequestId,
      targetStatus,
    })
      .then(() => {
        successDefault(
          targetStatus === 'IN_PROGRESS'
            ? '対応を開始しました'
            : targetStatus === 'REJECTED'
              ? '否決しました'
              : '取消しました',
        );
        setConfirmDialog(null);
        onUpdated('status');
      })
      .catch((e: Error) => errorDefault('操作に失敗しました', e.message));
  };

  const handleApprove = () => {
    approveContractRequest({ contractRequestId: detail.contractRequestId })
      .then(() => {
        successDefault('承認しました');
        setConfirmDialog(null);
        onUpdated('status');
      })
      .catch((e: Error) => errorDefault('承認に失敗しました', e.message));
  };

  // マスタデータ読み込み（編集モード開始時）
  const loadMasterData = () => {
    if (masterLoaded) return Promise.resolve();
    return Promise.all([
      getMasterType('prefecture'),
      getMasterType('contract_type'),
      getMasterType('contract_unit'),
      getContractTemplate({ visibleOnly: true }),
    ])
      .then(([pref, ctype, cunit, tmpl]) => {
        setPrefectures(pref);
        setContractTypes(ctype);
        setContractUnits(cunit);
        setTemplateData(tmpl);
        setMasterLoaded(true);
      })
      .catch(() => errorDefault('マスタデータの取得に失敗しました'));
  };

  // 編集モード開始
  const handleStartEdit = () => {
    loadMasterData().then(() => {
      // 現在の値で初期化
      setEditPersonInChargeId(detail.personInChargeId);
      setEditPrefectureId(detail.prefectureId?.main || '');
      setEditLocation(detail.location?.main || '');
      setEditContractTypeId(detail.contractTypeId?.main || '');
      setEditContractStartDate(detail.contractStartDate?.main || '');
      setEditContractEndDate(detail.contractEndDate?.main || '');
      setEditCostAmount(detail.costAmount?.main != null ? String(detail.costAmount.main) : '');
      setEditSalesAmount(detail.salesAmount?.main != null ? String(detail.salesAmount.main) : '');
      setEditOperationRate(
        detail.operationRate?.main != null ? String(detail.operationRate.main) : '',
      );
      setEditContractUnitId(detail.contractUnitId?.main || '');
      setEditAdjustmentUpperLimit(
        detail.adjustmentUpperLimit?.main != null ? String(detail.adjustmentUpperLimit.main) : '',
      );
      setEditAdjustmentLowerLimit(
        detail.adjustmentLowerLimit?.main != null ? String(detail.adjustmentLowerLimit.main) : '',
      );
      setEditNeedsOriginSheet(detail.needsOriginSheet?.main || false);
      setEditOriginSheetDestination(detail.originSheetDestination?.main || '');
      setEditRemarks(detail.remarks || '');
      // テンプレート項目初期化
      const tmplVals: Record<string, string> = {};
      if (detail.templateItems) {
        Object.entries(detail.templateItems).forEach(([key, item]) => {
          if (item.mainValues && item.mainValues.length > 0) {
            tmplVals[key] =
              item.mainValues.length === 1 ? item.mainValues[0] : JSON.stringify(item.mainValues);
          }
        });
      }
      setEditTemplateValues(tmplVals);
      // 技術スタックIDの初期化
      const techIds: string[] = [];
      const skillGroup = templateData?.results.find((g) => g.groupId === 'FIXED_MEMBER_SKILL');
      if (skillGroup && detail.templateItems) {
        skillGroup.items.forEach((item) => {
          const tmplItem = detail.templateItems[item.itemKey];
          if (tmplItem?.mainValues) {
            techIds.push(...tmplItem.mainValues);
          }
        });
      }
      setEditTechStackIds(techIds);
      setIsEditMode(true);
      onEditModeChange?.(true);
    });
  };

  // 編集キャンセル
  const handleCancelEdit = () => {
    setIsEditMode(false);
    onEditModeChange?.(false);
  };

  // 保存パラメータ構築
  const buildUpdateParams = (): UpdateContractRequestParams => {
    const templateItems: TemplateItemInput[] = Object.entries(editTemplateValues)
      .filter(([, v]) => v)
      .map(([key, value]) => {
        let values: string[];
        try {
          const parsed = JSON.parse(value);
          values = Array.isArray(parsed) ? parsed : [value];
        } catch {
          values = [value];
        }
        return { itemKey: key, values };
      });

    // 技術スタックIDをテンプレート項目に追加
    if (editTechStackIds.length > 0) {
      const skillGroup = templateGroups.find((g) => g.groupId === 'FIXED_MEMBER_SKILL');
      if (skillGroup && skillGroup.items.length > 0) {
        skillGroup.items.forEach((item) => {
          templateItems.push({ itemKey: item.itemKey, values: editTechStackIds });
        });
      }
    }

    return {
      contractRequestId: detail.contractRequestId,
      personInChargeId: editPersonInChargeId || undefined,
      prefectureId: editPrefectureId || undefined,
      location: editLocation || undefined,
      contractTypeId: editContractTypeId || undefined,
      contractStartDate: editContractStartDate || undefined,
      contractEndDate: editContractEndDate || undefined,
      costAmount: editCostAmount ? Number(editCostAmount) : undefined,
      salesAmount: editSalesAmount ? Number(editSalesAmount) : undefined,
      operationRate: editOperationRate ? Number(editOperationRate) : undefined,
      contractUnitId: editContractUnitId || undefined,
      adjustmentUpperLimit: editAdjustmentUpperLimit ? Number(editAdjustmentUpperLimit) : undefined,
      adjustmentLowerLimit: editAdjustmentLowerLimit ? Number(editAdjustmentLowerLimit) : undefined,
      needsOriginSheet: editNeedsOriginSheet || undefined,
      originSheetDestination: editOriginSheetDestination || undefined,
      remarks: editRemarks || undefined,
      templateItems: templateItems.length > 0 ? templateItems : undefined,
    };
  };

  // 保存（一時保存）
  const handleSave = () => {
    const params = buildUpdateParams();
    updateContractRequest(params)
      .then(() => {
        successDefault('保存しました');
        setIsEditMode(false);
        onEditModeChange?.(false);
        onUpdated('content');
      })
      .catch((e: Error) => errorDefault('保存に失敗しました', e.message));
  };

  // 再申請クリック（編集モード: 保存してからダイアログ表示）
  const handleResubmitClick = () => {
    const params = buildUpdateParams();
    updateContractRequest(params)
      .then(() => {
        setSubmitConfirmOpen(true);
      })
      .catch((e: Error) => errorDefault('保存に失敗しました', e.message));
  };

  // 申請クリック（閲覧モード: 直接ダイアログ表示）
  const handleSubmitFromView = () => {
    setSubmitConfirmOpen(true);
  };

  // 再申請確認
  const handleResubmitConfirm = (data: ConfirmDialogResult) => {
    setSubmitConfirmOpen(false);
    updateContractRequestStatus({
      contractRequestId: detail.contractRequestId,
      targetStatus: 'SUBMITTED',
      applicantUserIds: data.applicantUserIds,
      destinationUserIds: data.destinationUserIds,
      validStartDate: data.validStartDate,
    })
      .then(() => {
        successDefault('申請しました');
        setIsEditMode(false);
        onEditModeChange?.(false);
        onUpdated('content');
      })
      .catch((e: Error) => errorDefault('申請に失敗しました', e.message));
  };

  // 都道府県コード→名前解決
  const getPrefectureName = (code: string | null | undefined): string => {
    if (!code) return '—';
    const found = prefectures.find((p) => p.typeCode === code);
    return found ? found.typeLabel : code;
  };

  // ユーザーリスト
  const managers = useMemo(() => users.filter((u) => u.roles.includes('MANAGER')), [users]);
  const backOfficeUsers = useMemo(
    () => users.filter((u) => u.roles.includes('BACK_OFFICE')),
    [users],
  );

  // セレクトオプション
  const prefectureOptions: SelectOption[] = useMemo(
    () => prefectures.map((p) => ({ value: p.typeCode, label: p.typeLabel })),
    [prefectures],
  );

  // テンプレートグループ変換
  const templateGroups: TemplateGroup[] = useMemo(() => {
    if (!templateData) return [];
    return templateData.results
      .filter((g) => g.sortOrder >= 0 || g.groupId === 'FIXED_MEMBER_SKILL')
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((g) => ({
        groupId: g.groupId,
        name: g.name,
        items: g.items
          .filter((i) => !i.isDeleted)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((i) => ({
            itemKey: i.itemKey,
            label: i.label,
            formType: i.formType,
            selectItems: i.selectItems
              .filter((s) => !s.isDeleted)
              .map((s) => ({ code: s.code, name: s.name })),
          })),
      }));
  }, [templateData]);

  // 閲覧モード用: templateItemsから技術スタックIDを抽出
  const viewTechStackIds: string[] = useMemo(() => {
    if (!detail.templateItems || !templateData) return [];
    const skillGroup = templateData.results.find((g) => g.groupId === 'FIXED_MEMBER_SKILL');
    if (!skillGroup) return [];
    const ids: string[] = [];
    skillGroup.items.forEach((item) => {
      const tmplItem = detail.templateItems[item.itemKey];
      if (tmplItem?.mainValues) {
        ids.push(...tmplItem.mainValues);
      }
    });
    return ids;
  }, [detail.templateItems, templateData]);

  const handleTemplateValueChange = (itemKey: string, value: string) => {
    setEditTemplateValues((prev) => ({ ...prev, [itemKey]: value }));
  };

  // 保存バリデーション（基本情報は編集不可なので常にtrue）
  const isSaveValid = true;
  // 申請バリデーション（編集モード用）
  const isSubmitValid = Boolean(
    editContractStartDate &&
    editContractEndDate &&
    editPrefectureId &&
    editContractTypeId &&
    editCostAmount &&
    editSalesAmount &&
    editContractUnitId,
  );
  // 申請バリデーション（閲覧モード用 - detailの値で判定）
  const isSubmitValidFromView = Boolean(
    detail.contractStartDate?.main &&
    detail.contractEndDate?.main &&
    detail.prefectureId?.main &&
    detail.contractTypeId?.main &&
    detail.costAmount?.main != null &&
    detail.salesAmount?.main != null &&
    detail.contractUnitId?.main,
  );

  if (isEditMode) {
    return (
      <Stack spacing={2}>
        {/* 編集モードヘッダー（固定） */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            pb: 1,
            mx: -2,
            px: 2,
            pt: 1,
            mt: -1,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {detail.contractManagementId && (
                <Typography variant="body2" color="text.secondary">
                  {detail.contractManagementId} /
                </Typography>
              )}
              <Typography variant="h6" fontWeight="bold">
                {detail.managementId}
              </Typography>
              <RequestTypeChip requestType={detail.requestType} />
              <RequestStatusChip requestStatus={detail.requestStatus} />
            </Stack>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary">
                作成者: {detail.createdByName} 作成日: {formatDate(detail.createdAt)}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                更新者: {detail.updatedByName} 更新日: {formatDate(detail.updatedAt)}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancelEdit}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              キャンセル
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!isSaveValid}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              保存
            </Button>
            {canResubmit && (
              <Button
                variant="contained"
                size="small"
                startIcon={<SendIcon />}
                onClick={handleResubmitClick}
                disabled={!isSubmitValid}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                申請
              </Button>
            )}
          </Stack>
        </Box>

        {/* 基本情報（読み取り専用） */}
        <DetailSection title="基本情報">
          <Grid container spacing={1.5}>
            <Grid size={6}>
              <UserSelect
                mode="single"
                users={users}
                selectedUserId={editPersonInChargeId}
                onChange={setEditPersonInChargeId}
                label="担当者"
              />
            </Grid>
            <DetailField label="課長" value={detail.managerName} asInput />
            <DetailField label="プロジェクト" value={detail.projectName} asInput />
            <DetailField label="取引先" value={detail.clientName} asInput />
          </Grid>
        </DetailSection>

        {/* メンバー情報 */}
        <DetailSection title="メンバー情報">
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField
                size="small"
                fullWidth
                label="メンバー"
                value={detail.memberName}
                disabled
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                size="small"
                fullWidth
                label="パートナー"
                value={detail.partnerName || '社員'}
                disabled
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid size={6}>
              <Stack spacing={2}>
                <CustomSelect
                  mode="single"
                  options={prefectureOptions}
                  value={editPrefectureId}
                  onChange={setEditPrefectureId}
                  label="都道府県"
                />
                <TextField
                  size="small"
                  fullWidth
                  label="ロケーション"
                  value={editLocation}
                  onChange={(e) => setEditLocation(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>
            </Grid>
          </Grid>
        </DetailSection>

        {/* 契約情報 */}
        <DetailSection title="契約情報">
          <Grid container spacing={2}>
            <Grid size={6}>
              <FormLabel component="legend" sx={{ mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  契約形態
                </Typography>
              </FormLabel>
              <RadioGroup
                row
                value={editContractTypeId}
                onChange={(e) => setEditContractTypeId(e.target.value)}
              >
                {contractTypes
                  .filter((ct) => ct.typeCode !== 'EMPLOYEE')
                  .map((ct) => (
                    <FormControlLabel
                      key={ct.typeCode}
                      value={ct.typeCode}
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{ct.typeLabel}</Typography>}
                    />
                  ))}
              </RadioGroup>
            </Grid>
            <Grid size={6}>
              <FormLabel component="legend" sx={{ mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  契約単位
                </Typography>
              </FormLabel>
              <RadioGroup
                row
                value={editContractUnitId}
                onChange={(e) => setEditContractUnitId(e.target.value)}
              >
                {contractUnits.map((cu) => (
                  <FormControlLabel
                    key={cu.typeCode}
                    value={cu.typeCode}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{cu.typeLabel}</Typography>}
                  />
                ))}
              </RadioGroup>
            </Grid>
            <Grid size={6}>
              <FinancialYearMonthSelect
                dateType="start"
                value={editContractStartDate}
                onChange={setEditContractStartDate}
                label="契約開始日"
                required
              />
            </Grid>
            <Grid size={6}>
              <FinancialYearMonthSelect
                dateType="end"
                value={editContractEndDate}
                onChange={setEditContractEndDate}
                label="契約終了日"
                required
              />
            </Grid>
            <Grid size={6}>
              <AmountField
                value={editCostAmount}
                onChange={setEditCostAmount}
                label="契約金額（原価）"
              />
            </Grid>
            <Grid size={6}>
              <AmountField
                value={editSalesAmount}
                onChange={setEditSalesAmount}
                label="契約金額（売上）"
              />
            </Grid>
            <Grid size={6}>
              <PercentField
                value={editOperationRate}
                onChange={setEditOperationRate}
                label="稼働率"
              />
            </Grid>
            <Grid size={6} />
            <Grid size={6}>
              <HourField
                value={editAdjustmentUpperLimit}
                onChange={setEditAdjustmentUpperLimit}
                label="精算幅（上限）"
              />
            </Grid>
            <Grid size={6}>
              <HourField
                value={editAdjustmentLowerLimit}
                onChange={setEditAdjustmentLowerLimit}
                label="精算幅（下限）"
              />
            </Grid>
          </Grid>
        </DetailSection>

        {/* 補足情報 */}
        <DetailSection title="補足情報">
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editNeedsOriginSheet}
                    onChange={(e) => setEditNeedsOriginSheet(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">原籍確認シート: 必要</Typography>}
              />
            </Grid>
            {editNeedsOriginSheet && (
              <Grid size={12}>
                <TextField
                  size="small"
                  fullWidth
                  label="原籍確認シート提出先"
                  value={editOriginSheetDestination}
                  onChange={(e) => setEditOriginSheetDestination(e.target.value)}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            )}
          </Grid>
        </DetailSection>

        {/* 特記事項 */}
        <DetailSection title="特記事項">
          <TextArea
            value={editRemarks}
            onChange={setEditRemarks}
            maxLength={10000}
            rows={4}
            fullWidth
          />
        </DetailSection>

        {/* テンプレート項目 */}
        {templateGroups.length > 0 && (
          <TemplateGroupView
            groups={templateGroups}
            mode="edit"
            values={editTemplateValues}
            onValueChange={handleTemplateValueChange}
            techStackIds={editTechStackIds}
            onTechStackChange={setEditTechStackIds}
            defaultExpanded
          />
        )}

        {/* 再申請確認ダイアログ */}
        <ContractConfirmDialog
          open={submitConfirmOpen}
          onClose={() => setSubmitConfirmOpen(false)}
          onConfirm={handleResubmitConfirm}
          type={detail.requestType === 'CHANGE' ? 'change' : 'create'}
          managers={managers}
          backOfficeUsers={backOfficeUsers}
          currentUserId={userSession.userId}
          contractStartDate={editContractStartDate}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {/* ヘッダー（固定） */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1,
          mx: -2,
          px: 2,
          pt: 1,
          mt: -1,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {detail.contractManagementId && (
              <Typography variant="body2" color="text.secondary">
                {detail.contractManagementId} /
              </Typography>
            )}
            <Typography variant="h6" fontWeight="bold">
              {detail.managementId}
            </Typography>
            <RequestTypeChip requestType={detail.requestType} />
            <RequestStatusChip requestStatus={detail.requestStatus} />
          </Stack>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              作成者: {detail.createdByName} 作成日: {formatDate(detail.createdAt)}
            </Typography>
            <br />
            <Typography variant="caption" color="text.secondary">
              更新者: {detail.updatedByName} 更新日: {formatDate(detail.updatedAt)}
            </Typography>
          </Box>
        </Stack>

        {/* アクションボタン */}
        <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
          {showEdit && (
            <Button
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleStartEdit}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              編集
            </Button>
          )}
          {showStart && (
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={() => handleStatusChange('IN_PROGRESS')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              対応開始
            </Button>
          )}
          {showReject && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => setConfirmDialog('reject')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              否決
            </Button>
          )}
          {showApprove && (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => setConfirmDialog('approve')}
              disabled={!approveEnabled}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              承認
            </Button>
          )}
          {showCancel && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleStatusChange('CANCELLED')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              取消
            </Button>
          )}
          {canResubmit && (
            <Button
              size="small"
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSubmitFromView}
              disabled={!isSubmitValidFromView}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              申請
            </Button>
          )}
          {showDuplicate && (
            <Box sx={{ ml: 'auto !important' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={() => setIsDuplicateDialogOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                複製
              </Button>
            </Box>
          )}
          <Box sx={{ ml: showDuplicate ? undefined : 'auto !important' }}>
            <ContractPasteButton data={detail} dataType="contractRequest" />
          </Box>
        </Stack>
      </Box>

      {/* 申請情報 */}
      <DetailSection title="申請情報">
        <Grid container spacing={1.5}>
          <DetailField label="申請者" value={detail.applicants.map((a) => a.userName).join(', ')} />
          <DetailField
            label="申請日"
            value={detail.submittedAt ? formatDateTime(detail.submittedAt) : '—'}
          />
          <Grid size={12}>
            <Typography variant="caption" color="text.secondary">
              申請先
            </Typography>
            <Typography variant="body2">
              {detail.destinations.map((d) => d.userName).join(', ') || '—'}
            </Typography>
          </Grid>
          <DetailField label="承認者" value={detail.approvedByName || '—'} />
          <DetailField
            label="承認日"
            value={detail.approvedAt ? formatDateTime(detail.approvedAt) : '—'}
          />
        </Grid>
      </DetailSection>

      {/* 基本情報 */}
      <DetailSection title="基本情報">
        <Grid container spacing={1.5}>
          <DetailField label="担当者" value={detail.personInChargeName} />
          <DetailField label="課長" value={detail.managerName} />
          <DetailField label="プロジェクト" value={detail.projectName} />
          <DetailField label="取引先" value={detail.clientName} />
        </Grid>
      </DetailSection>

      {/* メンバー情報 */}
      <DetailSection title="メンバー情報">
        <Grid container spacing={1.5}>
          <DetailField label="メンバー" value={detail.memberName} />
          <DetailField label="パートナー" value={detail.partnerName} />
          <DiffField
            label="都道府県"
            main={getPrefectureName(detail.prefectureId?.main)}
            sub={detail.prefectureId?.sub ? getPrefectureName(detail.prefectureId.sub) : undefined}
          />
          <DiffField
            label="ロケーション"
            main={detail.location?.main || '—'}
            sub={detail.location?.sub}
          />
        </Grid>
      </DetailSection>

      {/* 契約情報 */}
      <DetailSection title="契約情報">
        <Grid container spacing={1.5}>
          <DiffField
            label="契約形態"
            main={detail.contractTypeName || '—'}
            sub={detail.contractTypeId?.sub ? '(変更あり)' : undefined}
          />
          <DiffField
            label="契約単位"
            main={detail.contractUnitName || '—'}
            sub={detail.contractUnitId?.sub ? '(変更あり)' : undefined}
          />
          <DiffField
            label="契約開始日"
            main={detail.contractStartDate?.main ? formatDate(detail.contractStartDate.main) : '—'}
            sub={
              detail.contractStartDate?.sub ? formatDate(detail.contractStartDate.sub) : undefined
            }
          />
          <DiffField
            label="契約終了日"
            main={detail.contractEndDate?.main ? formatDate(detail.contractEndDate.main) : '—'}
            sub={detail.contractEndDate?.sub ? formatDate(detail.contractEndDate.sub) : undefined}
          />
          <DiffField
            label="契約金額（原価）"
            main={
              detail.costAmount?.main != null ? `${detail.costAmount.main.toLocaleString()}円` : '—'
            }
            sub={
              detail.costAmount?.sub != null
                ? `${detail.costAmount.sub.toLocaleString()}円`
                : undefined
            }
          />
          <DiffField
            label="契約金額（売上）"
            main={
              detail.salesAmount?.main != null
                ? `${detail.salesAmount.main.toLocaleString()}円`
                : '—'
            }
            sub={
              detail.salesAmount?.sub != null
                ? `${detail.salesAmount.sub.toLocaleString()}円`
                : undefined
            }
          />
          <DiffField
            label="稼働率"
            main={detail.operationRate?.main != null ? `${detail.operationRate.main}%` : '—'}
            sub={detail.operationRate?.sub != null ? `${detail.operationRate.sub}%` : undefined}
          />
          <Grid size={6} />
          <DiffField
            label="精算幅（上限）"
            main={
              detail.adjustmentUpperLimit?.main != null
                ? `${detail.adjustmentUpperLimit.main}h`
                : '—'
            }
            sub={
              detail.adjustmentUpperLimit?.sub != null
                ? `${detail.adjustmentUpperLimit.sub}h`
                : undefined
            }
          />
          <DiffField
            label="精算幅（下限）"
            main={
              detail.adjustmentLowerLimit?.main != null
                ? `${detail.adjustmentLowerLimit.main}h`
                : '—'
            }
            sub={
              detail.adjustmentLowerLimit?.sub != null
                ? `${detail.adjustmentLowerLimit.sub}h`
                : undefined
            }
          />
        </Grid>
      </DetailSection>

      {/* 備考 */}
      <DetailSection title="補足情報">
        <Grid container spacing={1.5}>
          <DiffField
            label="原籍確認シート"
            main={detail.needsOriginSheet?.main ? '必要' : '不要'}
            sub={
              detail.needsOriginSheet?.sub != null
                ? detail.needsOriginSheet.sub
                  ? '必要'
                  : '不要'
                : undefined
            }
          />
          <DiffField
            label="提出先"
            main={detail.originSheetDestination?.main || '—'}
            sub={detail.originSheetDestination?.sub}
          />
        </Grid>
      </DetailSection>

      {/* 特記事項 */}
      <DetailSection title="特記事項">
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {detail.remarks || '—'}
        </Typography>
      </DetailSection>

      {/* テンプレート項目 */}
      {templateGroups.length > 0 &&
        detail.templateItems &&
        Object.keys(detail.templateItems).length > 0 && (
          <TemplateGroupView
            groups={templateGroups}
            mode="preview"
            values={Object.fromEntries(
              Object.entries(detail.templateItems).map(([key, item]) => [
                key,
                item.mainValues.length === 1 ? item.mainValues[0] : JSON.stringify(item.mainValues),
              ]),
            )}
            diffValues={Object.fromEntries(
              Object.entries(detail.templateItems)
                .filter(([, item]) => item.subValues != null)
                .map(([key, item]) => [
                  key,
                  item.subValues!.length === 1
                    ? item.subValues![0]
                    : JSON.stringify(item.subValues),
                ]),
            )}
            techStackIds={viewTechStackIds}
            onValueChange={() => {}}
          />
        )}

      {/* 確認ダイアログ */}
      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
      >
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight={600}>
            {confirmDialog === 'approve' ? '契約申請を承認しますか？' : '契約申請を否決しますか？'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography variant="body2">管理ID: {detail.managementId}</Typography>
            <Typography variant="body2">申請種別: {RequestType[detail.requestType]}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialog(null)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color={confirmDialog === 'approve' ? 'success' : 'error'}
            onClick={() =>
              confirmDialog === 'approve' ? handleApprove() : handleStatusChange('REJECTED')
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {confirmDialog === 'approve' ? '承認' : '否決'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 申請確認ダイアログ（閲覧モード） */}
      <ContractConfirmDialog
        open={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        onConfirm={handleResubmitConfirm}
        type={detail.requestType === 'CHANGE' ? 'change' : 'create'}
        managers={managers}
        backOfficeUsers={backOfficeUsers}
        currentUserId={userSession.userId}
        contractStartDate={detail.contractStartDate?.main || ''}
      />

      {/* 複製ダイアログ */}
      <ContractRequestDuplicateDialog
        open={isDuplicateDialogOpen}
        onClose={() => setIsDuplicateDialogOpen(false)}
        sourceDetail={detail}
        members={members}
        onComplete={() => onUpdated('content')}
      />
    </Stack>
  );
};

// --- DetailSection ---
const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const [expanded, setExpanded] = useState(true);

  const handleToggle = () => setExpanded((prev) => !prev);

  // 目次（FormTocLayout）用のアンカー。タイトルから id を生成する。
  const tocId = `sec-${encodeURIComponent(title)}`;

  return (
    <Accordion
      id={tocId}
      data-toc-section
      data-toc-label={title}
      elevation={0}
      expanded={expanded}
      sx={{
        '&:before': { display: 'none' },
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        scrollMarginTop: 8,
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton size="small" onClick={handleToggle} sx={{ cursor: 'pointer' }}>
            <ExpandMoreIcon />
          </IconButton>
        }
        sx={{
          px: 2,
          minHeight: 40,
          flexDirection: 'row-reverse',
          gap: 1,
          cursor: 'default',
          '&.Mui-focusVisible': { bgcolor: 'transparent' },
          '& .MuiAccordionSummary-expandIconWrapper': { transform: 'none' },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'none' },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          onClick={handleToggle}
          sx={{ cursor: 'pointer' }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

// --- DetailField ---
const DetailField: React.FC<{ label: string; value: string; asInput?: boolean }> = ({
  label,
  value,
  asInput = false,
}) =>
  asInput ? (
    <Grid size={6}>
      <TextField
        size="small"
        fullWidth
        label={label}
        value={value}
        disabled
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
    </Grid>
  ) : (
    <Grid size={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  );

// --- DiffField (差分表示) ---
const DiffField: React.FC<{ label: string; main: string; sub?: string | null }> = ({
  label,
  main,
  sub,
}) => {
  const isDiff = sub != null && sub !== main;
  return (
    <Grid size={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={
          isDiff
            ? {
                color: 'success.main',
                fontWeight: 600,
              }
            : {}
        }
      >
        {main}
      </Typography>
    </Grid>
  );
};

export default ContractRequestDetail;
